/**
 * HomeHero — Feb-2026 pivot rewrite.
 *
 * The pre-pivot hero was purchase-first with a two-plan picker
 * (₹199 AI Makeover / ₹399 Expert-Audited). After the Feb-2026
 * strategic pivot the homepage sells a SINGLE plan:
 *
 *   Full Profile Makeover — ₹999 / $49 · one-time
 *
 * The hero collects Name + Email, fires a lead-capture ping, then
 * opens Razorpay Checkout SDK via ``openOneTimeCheckoutGuest``.
 * On successful payment the webhook auto-provisions the buyer's
 * account and emails a one-click magic-login link. The buyer picks
 * their LinkedIn PDF post-login \u2014 keeping the hero form short is
 * the whole point of the pivot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Star,
  ArrowRight,
  Loader2,
  Lock,
  CheckCircle2,
  Sparkles,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  detectCurrency,
  cachedGeoCountry,
  fetchServerCurrency,
  openOneTimeCheckoutGuest,
} from '../../lib/billing';
import { getReferralContext } from '../../lib/referralTracker';
import { trackLead } from '../../lib/analytics';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

// ---------------------------------------------------------------------------
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Trust-cluster faces from the same set used on /recruiter-visibility.
// Kept as decorative avatars — alt="" because the 4.9/5 label is the
// screen-reader signal.
const TRUST_AVATARS = [
  { src: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'MS' },
  { src: 'https://images.unsplash.com/photo-1603985156220-90fc062e0521?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'DM' },
  { src: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'AR' },
  { src: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'PR' },
  { src: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'DL' },
];

// Kept intentionally short — the RIGHT-hand card already shows a fuller
// feature list, so this trio is the "at a glance" reassurance next to
// the price.
const HERO_FEATURES = [
  'Analyse your PDF profile up to 3 times + rewritten Headline / About / Experience',
  'Create up to 10 thought-leadership LinkedIn posts (all types combined)',
  'Premium Resume Makeover (Includes Design & Download)',
  'Peer benchmark deep-dive + branded PDF & PPTX report',
];

const PLAN_PRICE_INR = 999;
const PLAN_PRICE_USD = 49;

export function HomeHero() {
  const [currency, setCurrency] = useState(() => detectCurrency());
  const [country, setCountry] = useState(() => cachedGeoCountry());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Server-side geo lock. Locks the currency to the caller's actual
  // country on first paint. We update local state only — never write
  // to CURRENCY_KEY (that key is reserved for EXPLICIT user overrides
  // via the pricing page toggle).
  useEffect(() => {
    let alive = true;
    fetchServerCurrency()
      .then((geo) => {
        if (!alive || !geo) return;
        setCountry(geo.country || '');
        if (geo.currency) setCurrency(geo.currency);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const priceLabel = useMemo(
    () => (currency === 'INR' ? `₹${PLAN_PRICE_INR.toLocaleString('en-IN')}` : `$${PLAN_PRICE_USD}`),
    [currency],
  );

  const isValidEmail = useCallback(
    (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim()),
    [],
  );

  const onCTA = useCallback(async () => {
    const nameNorm = (name || '').trim();
    const emailNorm = (email || '').trim().toLowerCase();
    if (!nameNorm || nameNorm.length < 2) {
      toast.error('Please add your name so we can personalise your welcome.');
      return;
    }
    if (!emailNorm || !isValidEmail(emailNorm)) {
      toast.error('Please add a valid email address so we can email your login link.');
      return;
    }
    setSubmitting(true);
    try {
      // Fire lead capture first so admin always has context even if the
      // buyer abandons the Razorpay modal. LinkedIn URL is captured
      // post-login (user picks their PDF on the dashboard) so we send
      // an empty string here for backward compat with the endpoint.
      await axios
        .post(`${BACKEND}/api/hero-lead`, {
          linkedin_url: '',
          email: emailNorm,
          name: nameNorm,
          plan_key: 'one_time',
          currency,
          referral: getReferralContext(),
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('[hero-lead] capture failed:', e?.message || e);
        });
      trackLead('homepage_hero', { plan: 'one_time', currency });

      // Open Razorpay Checkout modal directly. Webhook path
      // ``_handle_hosted_button_payment`` auto-creates the account and
      // emails a one-click magic-login link on successful capture.
      await openOneTimeCheckoutGuest({
        email: emailNorm,
        name: nameNorm,
        currency,
        ctaLabel: 'hero_one_time',
        onSuccess: () => {
          toast.success(
            'Payment received. We just emailed your one-click login link — check your inbox.',
          );
        },
      });
    } finally {
      setSubmitting(false);
    }
  }, [name, email, isValidEmail, currency]);

  const onPrimaryCta = useCallback(() => {
    // On mobile the checkout card sits below the fold — scroll to it.
    const el = document.querySelector('[data-testid="home-hero-checkout-card"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <section
      className="relative overflow-x-clip isolate"
      aria-label="Full Profile Makeover"
      id="home-hero"
      data-testid="home-hero"
    >
      {/* Flat white hero — the previous soft rose+blue gradient wash
          was removed on 2026-10-01 to match the rest of the site. */}

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10 py-10 sm:py-14 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14 items-center">
          {/* LEFT — Résumé × LinkedIn Makeover pitch */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="relative"
          >
            <p
              className="font-mono-edge text-[11px] sm:text-[12px] tracking-[0.28em] uppercase text-muted-foreground"
              data-testid="home-hero-eyebrow"
            >
              Résumé × LinkedIn Makeover
            </p>

            <h1
              className="mt-5 font-display font-semibold tracking-[-0.03em] leading-[1.05] text-foreground text-[36px] sm:text-[52px] lg:text-[60px]"
              data-testid="home-hero-headline"
            >
              Résumé and LinkedIn makeovers for{' '}
              <span className="text-primary">more interviews</span> and{' '}
              <span className="text-primary">better opportunities</span>.
            </h1>

            <p
              className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
              data-testid="home-hero-subline"
            >
              We combine AI and expert-curated insights to highlight your strengths,
              and help recruiters see your potential clearly.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                type="button"
                onClick={onPrimaryCta}
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground h-12 px-7 text-[14.5px] font-semibold hover:opacity-90 transition-opacity"
                data-testid="home-hero-primary-cta"
              >
                Start my makeover
                <ArrowRight size={16} aria-hidden="true" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2.5" aria-hidden="true">
                  {TRUST_AVATARS.map((a, i) => (
                    <Avatar key={i} className="h-8 w-8 ring-2 ring-background shadow-sm">
                      <AvatarImage src={a.src} alt="" className="object-cover" loading="lazy" />
                      <AvatarFallback className="bg-secondary text-foreground text-[10px] font-medium">
                        {a.fb}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={12} className="text-primary fill-primary" aria-hidden="true" />
                    ))}
                    <span className="text-[12px] font-medium text-foreground/85 ml-1">4.9 / 5</span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    Trusted by candidates hitting hiring managers this week.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — single-plan CTA card (unchanged copy / mechanics) */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="relative rounded-[22px] border border-primary/20 bg-card p-5 sm:p-6 text-foreground shadow-[0_40px_120px_-60px_hsl(220_40%_2%/0.35)]"
            data-testid="home-hero-checkout-card"
          >
            <span
              className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-mono-edge tracking-[0.24em] uppercase px-3 py-1 whitespace-nowrap shadow-sm"
              data-testid="home-hero-card-badge"
            >
              <Sparkles size={11} strokeWidth={2.4} aria-hidden="true" />
              Full Profile Makeover
            </span>

            <div
              className="mt-1 flex items-end justify-between gap-3"
              data-testid="home-hero-card-price-row"
            >
              <div className="min-w-0">
                <p className="font-mono-edge text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                  One-time · 15-day access
                </p>
                <h2
                  className="mt-1 text-[19px] sm:text-[21px] font-bold tracking-tight text-foreground leading-tight"
                  data-testid="home-hero-card-title"
                >
                  Everything you need, in one sitting.
                </h2>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[32px] sm:text-[38px] font-extrabold text-primary leading-none tabular-nums">
                  {priceLabel}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {currency === 'INR' ? `Approx. $${PLAN_PRICE_USD}` : `Charged in USD`}
                </p>
              </div>
            </div>

            <ul
              className="mt-4 space-y-2"
              data-testid="home-hero-card-features"
            >
              {HERO_FEATURES.map((f, i) => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-[12.5px] leading-snug text-foreground/80"
                  data-testid={`home-hero-card-feature-${i}`}
                >
                  <CheckCircle2
                    size={14}
                    className="mt-0.5 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4">
              <label
                htmlFor="home-hero-name"
                className="block text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-1"
              >
                Your name
              </label>
              <div className="relative">
                <User
                  size={14}
                  className="absolute inset-y-0 left-3 my-auto text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="home-hero-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Priya Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-10 rounded-xl bg-background border border-input text-foreground text-[13px] px-9 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none placeholder:text-muted-foreground/70"
                  data-testid="home-hero-name"
                />
              </div>
            </div>

            <div className="mt-3">
              <label
                htmlFor="home-hero-email"
                className="block text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-1"
              >
                Your email
              </label>
              <div className="relative">
                <Mail
                  size={14}
                  className="absolute inset-y-0 left-3 my-auto text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="home-hero-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 rounded-xl bg-background border border-input text-foreground text-[13px] px-9 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none placeholder:text-muted-foreground/70"
                  data-testid="home-hero-email"
                />
              </div>
              <p className="mt-1 text-[10.5px] text-muted-foreground leading-snug">
                We&apos;ll email your one-click login link the second payment clears —
                upload your LinkedIn PDF from the dashboard.
              </p>
            </div>

            <button
              type="button"
              onClick={onCTA}
              disabled={submitting}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-primary hover:opacity-90 disabled:opacity-70 text-primary-foreground text-[14.5px] font-bold h-11 transition-opacity"
              data-testid="home-hero-cta"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              Buy Now — {priceLabel}
              {!submitting && <ArrowRight size={16} />}
            </button>

            <p
              className="mt-3 text-center text-[11px] text-muted-foreground"
              data-testid="home-hero-expert-note"
            >
              Razorpay secure checkout opens instantly — login link is emailed the second your
              payment is captured.
            </p>
            <p className="mt-1.5 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
              <Lock size={10} className="text-muted-foreground/70" aria-hidden="true" />
              Secure processing · No auto-renew · GST invoice on request
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default HomeHero;
